import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Check, Trash2, UserPlus, XCircle } from 'lucide-react';
import { Timestamp as FsTimestamp } from 'firebase/firestore';
import {
  useAssignees,
  useAssigneeUserMappings,
  useProject,
  useTasks,
} from '../hooks/useFirestoreData';
import { useProjectInvitations } from '../hooks/useAdminData';
import { useWbsConstraints, useWbsItems } from '../hooks/useWbs';
import { useAuthState } from '../hooks/useAuth';
import { useIsAdmin } from '../hooks/useIsAdmin';
import { AssigneeChip } from '../components/common/AssigneeChip';
import {
  createAssignee,
  createAssigneeWithBacklogUser,
  indexBacklogByAssigneeId,
  localizeAssigneeMappingError,
  setAssigneeBacklogUser,
  setAssigneeUserMapping,
  type BacklogUserLink,
} from '../lib/assigneeOps';
import { proposeBacklogAutoMatches } from '../lib/backlogAutoMatch';
import { stripAssigneeName } from '@mct/shared';
import { applyAssigneeCatalogEdit } from '../lib/assigneeCatalogSave';
import {
  CatalogOptionsEditor,
  type CatalogOptionEditResult,
} from '../components/Wbs/CatalogOptionsEditor';
import { pickNextFreeColorIndex } from '../lib/colorPalette';
import {
  runUserMappingPick,
  type UserMappingPickApiResult,
} from '../lib/userMappingPick';
import { Button } from '../components/ui/Button';
import { DateInput } from '../components/ui/DateInput';
import { Input, Label } from '../components/ui/Input';
import { Spinner } from '../components/ui/Spinner';
import { Badge } from '../components/ui/Badge';
import { getDb } from '../lib/firebase';
import {
  changeMemberRole,
  removeMember,
  updateBacklogConfig,
  updateProject,
  updateProjectSchedules,
} from '../lib/firestore';
import {
  inviteUserToProject,
  listAllBacklogUsers,
  listBacklogProjects,
  looksLikeLegacyBacklogSecretName,
  revokeInvitation,
  saveBacklogApiKey,
  translateCallableError,
  type BacklogProjectListItem,
  type BacklogUserListItem,
} from '../lib/adminCallables';
import { BacklogProjectPicker } from '../components/Settings/BacklogProjectPicker';
import { ScheduledJobsSection } from '../components/Settings/ScheduledJobsSection';
import { updateWbsConstraints } from '../lib/wbs';
import { tsToDate } from '../lib/wbsGantt';
import { cn } from '../lib/utils';
import { validateInvitationForm } from '../lib/invitationForm';
import type {
  NotificationDailyHour,
  ProjectDoc,
  Role,
  Timestamp as SharedTimestamp,
  WbsHoliday,
} from '@mct/shared';
import {
  DEFAULT_BACKLOG_SYNC_SETTINGS,
  DEFAULT_NOTIFICATION_SETTINGS,
  NOTIFICATION_DAILY_HOURS,
  ROLE_LABELS_JA,
  canManageProjectSettings,
  formatBacklogProjectLabel,
  invitableRolesFor,
} from '@mct/shared';

export function SettingsPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const { data: project, loading } = useProject(projectId ?? null);
  const { data: invitations } = useProjectInvitations(projectId ?? null);
  const { user } = useAuthState();

  // Project basic info
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');

  // Invite form
  const [mEmail, setMEmail] = useState('');
  const [mRole, setMRole] = useState<Role>('viewer');
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteMessage, setInviteMessage] = useState<string | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);

  // Backlog
  const [spaceUrl, setSpaceUrl] = useState('');
  const [projectKeys, setProjectKeys] = useState<string[]>([]);
  const [newProjectKey, setNewProjectKey] = useState('');
  // Persisted secret name comes off the project doc; we never display
  // it to the user. Whether the project ALREADY has an API key saved
  // is the only bit the UI needs to decide between "input visible" vs
  // "mask visible".
  const persistedSecretName = project?.backlog?.apiKeySecretName ?? '';
  const hasPersistedApiKey = persistedSecretName.length > 0;
  const isLegacySecretName = looksLikeLegacyBacklogSecretName(
    persistedSecretName,
  );
  // Edit-mode state for the API key field. Defaults to "input visible"
  // when no key is saved yet (or when the saved value is the legacy
  // shape we need to overwrite); flips back to "masked" after a
  // successful save.
  const [apiKeyEditing, setApiKeyEditing] = useState<boolean>(true);
  const [apiKeyDraft, setApiKeyDraft] = useState('');
  const [backlogBusy, setBacklogBusy] = useState(false);
  const [backlogSaved, setBacklogSaved] = useState<string | null>(null);
  const [backlogError, setBacklogError] = useState<string | null>(null);
  // Backlog project picker (replaces the legacy manual-key chip input).
  const [backlogProjectList, setBacklogProjectList] = useState<
    BacklogProjectListItem[] | null
  >(null);
  const [backlogProjectListFetching, setBacklogProjectListFetching] =
    useState(false);
  const [backlogProjectListError, setBacklogProjectListError] = useState<
    string | null
  >(null);
  // Diagnostic fallback: when API enumeration fails, surface the legacy
  // hand-typed chip input so the admin can still save a config.
  const [backlogManualMode, setBacklogManualMode] = useState(false);

  // Schedules (daily summary + Backlog sync toggles)
  const [notifEnabled, setNotifEnabled] = useState<boolean>(
    DEFAULT_NOTIFICATION_SETTINGS.enabled,
  );
  const [notifHour, setNotifHour] = useState<NotificationDailyHour>(
    DEFAULT_NOTIFICATION_SETTINGS.dailyHour,
  );
  const [notifyOnComplete, setNotifyOnComplete] = useState<boolean>(
    DEFAULT_NOTIFICATION_SETTINGS.notifyOnTaskComplete ?? true,
  );
  const [backlogSyncEnabled, setBacklogSyncEnabled] = useState<boolean>(
    DEFAULT_BACKLOG_SYNC_SETTINGS.enabled,
  );
  const [scheduleSaved, setScheduleSaved] = useState(false);

  useEffect(() => {
    if (project) {
      setName(project.name);
      setDesc(project.description);
      if (project.backlog) {
        setSpaceUrl(project.backlog.spaceUrl);
        // Backwards-compat: pre-migration prod docs still hold the
        // singular `backlogProjectKey` and have no `backlogProjectKeys`
        // field at all. Reading `.backlogProjectKeys` directly therefore
        // yields `undefined`, and the downstream `.length` reads
        // crash-and-blank-screen the whole settings page. Coalesce both
        // shapes here so un-migrated documents survive until they're
        // overwritten with a multi-key save.
        const legacy = project.backlog as {
          backlogProjectKeys?: string[];
          backlogProjectKey?: string;
        };
        const keys = Array.isArray(legacy.backlogProjectKeys)
          ? legacy.backlogProjectKeys
          : legacy.backlogProjectKey
          ? [legacy.backlogProjectKey]
          : [];
        setProjectKeys(keys);
        // We never seed the API-key draft from the project doc — the
        // saved value is either a Secret Manager NAME (worthless to
        // display) or, for legacy bad rows, the API key body itself
        // (which we explicitly avoid surfacing). The mask handles
        // either case; the user has to click 「変更する」 to type a
        // new key.
        setApiKeyDraft('');
        // If the project already has a sane secret name saved, start
        // collapsed (mask visible). If nothing is saved yet OR the
        // saved value looks like the legacy bug, start expanded so
        // the user can paste a new key.
        const sane =
          typeof project.backlog.apiKeySecretName === 'string' &&
          project.backlog.apiKeySecretName.startsWith('backlog-api-key-') &&
          project.backlog.apiKeySecretName.length <= 120;
        setApiKeyEditing(!sane);
      }
      const n = project.notifications ?? DEFAULT_NOTIFICATION_SETTINGS;
      setNotifEnabled(n.enabled);
      setNotifHour(n.dailyHour);
      // Legacy docs without the field default to ON (opt-out).
      setNotifyOnComplete(n.notifyOnTaskComplete !== false);
      const b = project.backlogSync ?? DEFAULT_BACKLOG_SYNC_SETTINGS;
      setBacklogSyncEnabled(b.enabled);
    }
  }, [project]);

  // Keep the invite-form role within what the current viewer is allowed to issue.
  const myUid = user?.uid;
  useEffect(() => {
    if (!project || !myUid) return;
    const role = project.memberRoles?.[myUid];
    const allowed = invitableRolesFor(role);
    const fallback = allowed[0];
    if (fallback && !allowed.includes(mRole)) {
      setMRole(fallback);
    }
  }, [project, myUid, mRole]);

  // useIsAdmin must run before any conditional return to keep the hook
  // call order stable across renders (React rules-of-hooks). The bypass
  // is only consulted further down inside canEditSettings, so the value
  // is harmless when the component is still loading.
  const { isAdmin: isPlatformAdmin } = useIsAdmin();

  if (!projectId) return null;
  if (loading || !project) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const roleEntries = Object.entries(project.members);
  const validation = validateInvitationForm(mEmail, mRole);
  const myRole: Role | undefined = user
    ? project.memberRoles?.[user.uid]
    : undefined;
  // Platform admins (custom claim) bypass the project-role gate so
  // support contexts can fix bindings even when the admin isn't
  // listed as a project member. Mirrors the rules-side `isAdmin()`
  // bypass we added for `assigneeUserMap`.
  const canEditSettings = canManageProjectSettings(myRole) || isPlatformAdmin;
  const invitableRoles = invitableRolesFor(myRole);
  const canInvite = invitableRoles.length > 0;
  const canManageMembers = myRole === 'owner' || myRole === 'admin';

  const onInvite = async () => {
    if (!validation.ok) return;
    setInviteBusy(true);
    setInviteMessage(null);
    setInviteError(null);
    try {
      const result = await inviteUserToProject(
        projectId,
        validation.email,
        validation.role,
      );
      setInviteMessage(
        result.path === 'direct-add'
          ? 'メンバーを追加しました。'
          : '招待を送信しました。',
      );
      setMEmail('');
    } catch (e) {
      setInviteError(translateCallableError(e).message);
    } finally {
      setInviteBusy(false);
    }
  };

  const onRevoke = async (invitationId: string) => {
    try {
      await revokeInvitation(invitationId);
    } catch (e) {
      setInviteError(translateCallableError(e).message);
    }
  };

  return (
    <div className="h-full overflow-auto px-6 py-6">
      <div className="mx-auto max-w-3xl space-y-8">
        <section className="card p-4">
          <h2 className="text-lg font-semibold">プロジェクト情報</h2>
          {!canEditSettings ? (
            <p className="mt-1 text-xs text-slate-500">
              編集にはオーナー / 管理者権限が必要です。
            </p>
          ) : null}
          <div className="mt-3 space-y-3">
            <div>
              <Label htmlFor="p-name">名前</Label>
              <Input
                id="p-name"
                value={name}
                readOnly={!canEditSettings}
                disabled={!canEditSettings}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="p-desc">説明</Label>
              <Input
                id="p-desc"
                value={desc}
                readOnly={!canEditSettings}
                disabled={!canEditSettings}
                onChange={(e) => setDesc(e.target.value)}
              />
            </div>
            {canEditSettings ? (
              <div className="flex justify-end">
                <Button
                  onClick={() => updateProject(getDb(), projectId, { name, description: desc })}
                >
                  保存
                </Button>
              </div>
            ) : null}
          </div>
        </section>

        <section className="card p-4">
          <h2 className="text-lg font-semibold">メンバー</h2>
          <div className="mt-3 space-y-2">
            {roleEntries.map(([uid, m]) => (
              <div
                key={uid}
                className="flex items-center justify-between rounded border border-slate-200 px-3 py-2"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">
                    {m.displayName}
                  </div>
                  <div className="truncate text-xs text-slate-500">{m.email}</div>
                </div>
                <div className="flex items-center gap-2">
                  {uid === project.ownerUserId ? (
                    <Badge tone="blue">{ROLE_LABELS_JA.owner}</Badge>
                  ) : canManageMembers ? (
                    <select
                      className="input !py-1"
                      value={m.role}
                      onChange={(e) =>
                        changeMemberRole(getDb(), project, uid, e.target.value as Role)
                      }
                    >
                      <option value="admin">{ROLE_LABELS_JA.admin}</option>
                      <option value="editor">{ROLE_LABELS_JA.editor}</option>
                      <option value="viewer">{ROLE_LABELS_JA.viewer}</option>
                    </select>
                  ) : (
                    <Badge tone="slate">{ROLE_LABELS_JA[m.role]}</Badge>
                  )}
                  {uid !== project.ownerUserId && canManageMembers ? (
                    <Button
                      variant="ghost"
                      className="!px-2"
                      aria-label="削除"
                      onClick={() => removeMember(getDb(), project, uid)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>

          {canInvite ? (
            <div className="mt-6 border-t border-slate-200 pt-4">
              <h3 className="text-sm font-semibold">メンバーを招待</h3>
              <p className="mt-1 text-xs text-slate-500">
                メールアドレスで招待します。未登録のユーザーは、初回サインイン時に自動で参加します。
              </p>
              <div className="mt-3 grid grid-cols-[1fr_auto] gap-3">
                <div>
                  <Label htmlFor="invite-email">メール</Label>
                  <Input
                    id="invite-email"
                    type="email"
                    value={mEmail}
                    onChange={(e) => setMEmail(e.target.value)}
                    placeholder="name@example.com"
                  />
                </div>
                <div>
                  <Label htmlFor="invite-role">ロール</Label>
                  <select
                    id="invite-role"
                    className="input"
                    value={mRole}
                    onChange={(e) => setMRole(e.target.value as Role)}
                  >
                    {invitableRoles.map((r) => (
                      <option key={r} value={r}>
                        {ROLE_LABELS_JA[r]}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {inviteMessage ? (
                <p className="mt-2 text-xs text-green-700">{inviteMessage}</p>
              ) : null}
              {inviteError ? (
                <p className="mt-2 text-xs text-red-600">{inviteError}</p>
              ) : null}
              <div className="mt-3 flex justify-end">
                <Button
                  disabled={!validation.ok || inviteBusy}
                  onClick={onInvite}
                >
                  <UserPlus className="mr-1 h-4 w-4" /> 招待
                </Button>
              </div>
            </div>
          ) : (
            <p className="mt-4 text-xs text-slate-500">
              招待にはオーナー / 管理者 / 編集者権限が必要です。
            </p>
          )}

          {/* Pending invitations list — visible to ALL project members
              (owner / admin / editor / viewer) so anyone can see who's
              been invited and by whom. The 取り消し button is gated to
              canManageMembers since revoking is a privileged action. */}
          <div className="mt-6 border-t border-slate-200 pt-4">
            <h3 className="text-sm font-semibold">保留中の招待</h3>
            {invitations.length === 0 ? (
              <p className="mt-2 text-xs text-slate-500">
                保留中の招待はありません。
              </p>
            ) : (
              <div className="mt-2 space-y-2">
                {invitations.map((inv) => {
                  const inviter = project.members[inv.invitedByUserId];
                  const inviterLabel = inviter?.displayName ?? inviter?.email ?? '(退会済み)';
                  const invitedAt = inv.invitedAt?.toDate?.();
                  return (
                    <div
                      key={inv.invitationId}
                      className="flex items-center justify-between rounded border border-slate-200 px-3 py-2"
                    >
                      <div className="min-w-0">
                        <div className="truncate text-sm">{inv.email}</div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-slate-500">
                          <span>ロール: {ROLE_LABELS_JA[inv.role] ?? inv.role}</span>
                          <span>•</span>
                          <span>招待者: {inviterLabel}</span>
                          {invitedAt ? (
                            <>
                              <span>•</span>
                              <span className="tabular-nums">
                                {invitedAt.toLocaleDateString('ja-JP')}
                              </span>
                            </>
                          ) : null}
                        </div>
                      </div>
                      {canManageMembers ? (
                        <Button
                          variant="ghost"
                          className="!px-2"
                          aria-label="招待を取り消し"
                          onClick={() => onRevoke(inv.invitationId)}
                        >
                          <XCircle className="h-4 w-4 text-red-500" />
                          <span className="ml-1 text-xs">取り消し</span>
                        </Button>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {canEditSettings ? (
        <section className="card p-4">
          <h2 className="text-lg font-semibold">Backlog 連携</h2>
          <p className="mt-1 text-xs text-slate-500">
            Backlog の API
            キーを貼り付けるだけで OK
            です。サーバー側で Secret Manager
            に安全に保管します。
          </p>
          <div className="mt-3 space-y-3">
            <div>
              <Label>スペースURL</Label>
              <Input
                value={spaceUrl}
                onChange={(e) => setSpaceUrl(e.target.value)}
                placeholder="https://sunup.backlog.com"
              />
            </div>
            <div data-testid="backlog-apikey-field">
              <Label>Backlog API キー</Label>
              {apiKeyEditing ? (
                <>
                  {hasPersistedApiKey && isLegacySecretName ? (
                    <p
                      className="mt-1 rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800"
                      role="alert"
                      data-testid="backlog-apikey-legacy-warning"
                    >
                      既存の保存値は Secret Manager
                      に存在しません。これは API
                      キー本体を保存していた可能性があります。新しい
                      API
                      キーを入力して保存してください。古い値は破棄されます。
                    </p>
                  ) : null}
                  <Input
                    type="password"
                    autoComplete="off"
                    spellCheck={false}
                    value={apiKeyDraft}
                    onChange={(e) => setApiKeyDraft(e.target.value)}
                    placeholder="Backlog 個人設定 → API でコピーしたキー"
                    data-testid="backlog-apikey-input"
                  />
                  {hasPersistedApiKey ? (
                    <button
                      type="button"
                      className="mt-1 text-[11px] text-slate-500 underline-offset-2 hover:underline"
                      onClick={() => {
                        setApiKeyDraft('');
                        setApiKeyEditing(false);
                      }}
                    >
                      キャンセル
                    </button>
                  ) : null}
                </>
              ) : (
                <div
                  className="mt-1 flex items-center gap-3"
                  data-testid="backlog-apikey-mask"
                >
                  <span
                    className="inline-flex items-center gap-2 rounded border border-slate-200 bg-slate-50 px-2 py-1 font-mono text-xs text-slate-600"
                    aria-label="API キーは保存済み"
                  >
                    {'●'.repeat(8)}
                    <span className="text-[11px] text-green-700 font-sans">
                      保存済み
                    </span>
                  </span>
                  <button
                    type="button"
                    className="text-xs text-blue-700 underline-offset-2 hover:underline"
                    onClick={() => {
                      setApiKeyDraft('');
                      setApiKeyEditing(true);
                    }}
                    data-testid="backlog-apikey-edit"
                  >
                    変更する
                  </button>
                </div>
              )}
            </div>
            <div>
              <Label>連携プロジェクト</Label>
              <p className="mt-0.5 text-[11px] text-slate-500">
                スペースURLと API キーを保存した後、Backlog
                APIから取得してチェックで選択します。
                同じスペース内の複数プロジェクトを追跡する場合は複数選択できます。
              </p>
              {projectKeys.length > 0 ? (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {projectKeys.map((k) => {
                    // Prefer the API-resolved name from the most recent
                    // "プロジェクト一覧を取得" click; fall back to the
                    // snapshot persisted on the project doc so chips
                    // render names even on first page load (before the
                    // picker has been opened).
                    const liveName = backlogProjectList?.find(
                      (p) => p.projectKey === k,
                    )?.name;
                    const persistedName = project.backlog?.backlogProjectNames?.[k];
                    const displayName = liveName ?? persistedName ?? null;
                    return (
                      <span
                        key={k}
                        className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs"
                      >
                        <span>{formatBacklogProjectLabel(k, displayName)}</span>
                        <button
                          type="button"
                          aria-label={`${k} を削除`}
                          className="text-slate-400 hover:text-red-500"
                          onClick={() =>
                            setProjectKeys((prev) =>
                              prev.filter((x) => x !== k),
                            )
                          }
                        >
                          <XCircle className="h-3 w-3" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              ) : null}

              {backlogManualMode ? (
                <div className="mt-2">
                  <p className="text-[11px] text-slate-500">
                    手入力モード(診断用)。Backlog 上のプロジェクトキー(例:
                    <code className="mx-0.5">MIG</code>,
                    <code className="mx-0.5">APG</code>) を直接入力します。
                  </p>
                  <div className="mt-2 flex gap-2">
                    <Input
                      value={newProjectKey}
                      onChange={(e) =>
                        setNewProjectKey(e.target.value.toUpperCase())
                      }
                      onKeyDown={(e) => {
                        if (e.key !== 'Enter') return;
                        e.preventDefault();
                        const trimmed = newProjectKey.trim();
                        if (!trimmed) return;
                        if (projectKeys.includes(trimmed)) {
                          setNewProjectKey('');
                          return;
                        }
                        setProjectKeys((prev) => [...prev, trimmed]);
                        setNewProjectKey('');
                      }}
                      placeholder="MIG"
                    />
                    <Button
                      variant="secondary"
                      // The Japanese label "追加" used to wrap to two
                      // lines (追 / 加) when this row was a `flex
                      // gap-2`: the input next to it is `w-full`, which
                      // pushed the button to its min content width.
                      // The base `.btn` class doesn't ship with
                      // `whitespace-nowrap`, so the browser broke the
                      // 2-character label between glyphs. Pinning
                      // `whitespace-nowrap shrink-0` on the button
                      // forces single-line rendering at any viewport
                      // width.
                      className="shrink-0 whitespace-nowrap"
                      disabled={
                        !newProjectKey.trim() ||
                        projectKeys.includes(newProjectKey.trim())
                      }
                      onClick={() => {
                        const trimmed = newProjectKey.trim();
                        if (!trimmed) return;
                        setProjectKeys((prev) => [...prev, trimmed]);
                        setNewProjectKey('');
                      }}
                    >
                      追加
                    </Button>
                  </div>
                  <button
                    type="button"
                    className="mt-2 text-xs text-slate-500 underline-offset-2 hover:underline"
                    onClick={() => setBacklogManualMode(false)}
                  >
                    APIから取得モードに戻す
                  </button>
                </div>
              ) : (
                <div className="mt-2">
                  <BacklogProjectPicker
                    projects={backlogProjectList}
                    selectedKeys={projectKeys}
                    // The list callable depends on a sane secret being
                    // saved server-side. Block fetch until then —
                    // otherwise the user gets a confusing "secret-error"
                    // on the very first try after typing a key.
                    fetchDisabled={
                      !spaceUrl ||
                      !hasPersistedApiKey ||
                      isLegacySecretName ||
                      backlogBusy
                    }
                    fetching={backlogProjectListFetching}
                    fetchError={backlogProjectListError}
                    onFetch={async () => {
                      setBacklogProjectListError(null);
                      setBacklogProjectListFetching(true);
                      try {
                        const res = await listBacklogProjects(projectId);
                        if (res.ok) {
                          setBacklogProjectList(res.projects);
                        } else {
                          setBacklogProjectListError(res.message);
                        }
                      } catch (err) {
                        const t = translateCallableError(err);
                        setBacklogProjectListError(t.message);
                      } finally {
                        setBacklogProjectListFetching(false);
                      }
                    }}
                    onToggle={(projectKey, next) => {
                      setProjectKeys((prev) =>
                        next
                          ? prev.includes(projectKey)
                            ? prev
                            : [...prev, projectKey]
                          : prev.filter((x) => x !== projectKey),
                      );
                    }}
                    onSwitchToManual={() => setBacklogManualMode(true)}
                  />
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-3">
              {backlogSaved ? (
                <span className="text-xs text-green-700">{backlogSaved}</span>
              ) : null}
              {backlogError ? (
                <span className="text-xs text-red-700">{backlogError}</span>
              ) : null}
              <Button
                variant="secondary"
                disabled={backlogBusy || !project.backlog}
                onClick={async () => {
                  setBacklogError(null);
                  setBacklogSaved(null);
                  setBacklogBusy(true);
                  try {
                    await updateBacklogConfig(getDb(), projectId, null);
                    setSpaceUrl('');
                    setProjectKeys([]);
                    setNewProjectKey('');
                    setApiKeyDraft('');
                    setApiKeyEditing(true);
                    setBacklogSaved('連携を解除しました');
                    window.setTimeout(() => setBacklogSaved(null), 2500);
                  } catch (e) {
                    setBacklogError(
                      e instanceof Error ? e.message : '解除に失敗しました',
                    );
                  } finally {
                    setBacklogBusy(false);
                  }
                }}
              >
                連携を解除
              </Button>
              <Button
                disabled={
                  backlogBusy ||
                  !spaceUrl ||
                  // Either the user has an existing sane secret OR is
                  // typing a new key. We block on both being absent.
                  (!apiKeyEditing &&
                    (!hasPersistedApiKey || isLegacySecretName)) ||
                  (apiKeyEditing && apiKeyDraft.trim().length === 0)
                }
                onClick={async () => {
                  setBacklogError(null);
                  setBacklogSaved(null);
                  setBacklogBusy(true);
                  try {
                    // 1. If the user is editing the API key and has
                    //    typed something, persist it first via the
                    //    saveBacklogApiKey callable. This both writes
                    //    Secret Manager AND stamps the canonical
                    //    `backlog-api-key-{pid}` name onto the project
                    //    doc. We rely on that name below.
                    let secretName =
                      project.backlog?.apiKeySecretName ?? '';
                    if (apiKeyEditing && apiKeyDraft.trim().length > 0) {
                      const out = await saveBacklogApiKey(
                        projectId,
                        apiKeyDraft.trim(),
                      );
                      secretName = out.secretName;
                      // Clear draft and collapse to mask — keeps the
                      // raw key out of subsequent screen captures.
                      setApiKeyDraft('');
                      setApiKeyEditing(false);
                    }
                    if (!secretName) {
                      throw new Error('API キーを保存してください');
                    }
                    // 2. Save the rest of the config. We always write
                    //    the (possibly-just-rotated) canonical
                    //    secretName so a successful API-key save
                    //    overrides any legacy bad value.
                    //
                    //    Snapshot project names so every consumer
                    //    (settings chips on next reload, milestone-link
                    //    dialog, suggestion inbox, WBS filters) can
                    //    render `{name} ({key})` without re-hitting
                    //    Backlog. We start from the existing persisted
                    //    snapshot (preserves names for keys we still
                    //    have but didn't re-fetch this session), layer
                    //    the live list on top (fresh names override
                    //    stale ones), then drop entries for keys no
                    //    longer selected so renames + deselects both
                    //    take effect.
                    const nameByKey: Record<string, string> = {};
                    const priorNames = project.backlog?.backlogProjectNames ?? {};
                    for (const k of projectKeys) {
                      const live = backlogProjectList?.find(
                        (p) => p.projectKey === k,
                      )?.name;
                      const name = live ?? priorNames[k];
                      if (name && name.trim().length > 0) {
                        nameByKey[k] = name;
                      }
                    }
                    await updateBacklogConfig(getDb(), projectId, {
                      spaceUrl,
                      backlogProjectKeys: projectKeys,
                      backlogProjectNames: nameByKey,
                      apiKeySecretName: secretName,
                      lastSyncedAt: project.backlog?.lastSyncedAt ?? null,
                    });
                    setBacklogSaved('保存しました');
                    window.setTimeout(() => setBacklogSaved(null), 2500);
                  } catch (e) {
                    setBacklogError(translateCallableError(e).message);
                  } finally {
                    setBacklogBusy(false);
                  }
                }}
              >
                {backlogBusy ? '保存中…' : '保存'}
              </Button>
            </div>
          </div>
        </section>
        ) : null}

        {canEditSettings ? (
        <section className="card p-4">
          <h2 className="text-lg font-semibold">スケジュール / 通知</h2>
          <p className="mt-1 text-xs text-slate-500">
            定期実行の頻度はサービス全体で固定です。プロジェクト単位でON/OFFと、毎朝サマリーを送る時刻を選んでください。
          </p>

          <div className="mt-4 space-y-2">
            <h3 className="text-sm font-semibold">日次サマリーメール</h3>
            <p className="text-xs text-slate-500">
              指定時刻 (JST) に当日の担当タスクを担当者へ通知します。
            </p>
            <label className="mt-2 inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={notifEnabled}
                onChange={(e) => setNotifEnabled(e.target.checked)}
              />
              <span>このプロジェクトで日次通知を有効にする</span>
            </label>
            <div className="mt-2 flex items-center gap-2">
              <Label htmlFor="notif-hour" className="!mb-0">
                送信時刻
              </Label>
              <select
                id="notif-hour"
                className="input !py-1"
                value={notifHour}
                disabled={!notifEnabled}
                onChange={(e) =>
                  setNotifHour(Number(e.target.value) as NotificationDailyHour)
                }
              >
                {NOTIFICATION_DAILY_HOURS.map((h) => (
                  <option key={h} value={h}>
                    {String(h).padStart(2, '0')}:00 (JST)
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-6 space-y-2 border-t border-slate-200 pt-4">
            <h3 className="text-sm font-semibold">タスク完了通知</h3>
            <p className="text-xs text-slate-500">
              移行作業のタスクが完了したとき、次のタスク（後続）の担当者へ「着手できます」とメール通知します。担当者にユーザーが紐付いている場合のみ送信されます。
            </p>
            <label className="mt-2 inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={notifyOnComplete}
                disabled={!notifEnabled}
                onChange={(e) => setNotifyOnComplete(e.target.checked)}
              />
              <span>タスク完了時に後続担当者へ通知する</span>
            </label>
            {!notifEnabled ? (
              <p className="text-xs text-slate-400">
                上の通知が無効のあいだは送信されません。
              </p>
            ) : null}
          </div>

          {project.backlog ? (
            <div className="mt-6 border-t border-slate-200 pt-4">
              <h3 className="text-sm font-semibold">Backlog 同期</h3>
              <p className="text-xs text-slate-500">
                15分ごとの issue 同期と、60分ごとの活動取り込みをまとめて一時停止できます。Backlog
                連携設定そのものは保持されます。
              </p>
              <label className="mt-2 inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={backlogSyncEnabled}
                  onChange={(e) => setBacklogSyncEnabled(e.target.checked)}
                />
                <span>Backlog 同期を有効にする</span>
              </label>
            </div>
          ) : null}

          <div className="mt-4 flex items-center justify-end gap-3">
            {scheduleSaved ? (
              <span className="text-xs text-green-700">保存しました</span>
            ) : null}
            <Button
              onClick={async () => {
                setScheduleSaved(false);
                await updateProjectSchedules(getDb(), projectId, {
                  notifications: {
                    enabled: notifEnabled,
                    dailyHour: notifHour,
                    notifyOnTaskComplete: notifyOnComplete,
                  },
                  backlogSync: { enabled: backlogSyncEnabled },
                });
                setScheduleSaved(true);
                window.setTimeout(() => setScheduleSaved(false), 2500);
              }}
            >
              保存
            </Button>
          </div>
        </section>
        ) : null}

        <ScheduledJobsSection
          projectId={projectId}
          canEdit={canEditSettings}
        />

        <AssigneeMappingSection
          project={project}
          canEdit={canEditSettings}
        />

        <HolidaysSection
          projectId={projectId}
          canEdit={canEditSettings}
        />
      </div>
    </div>
  );
}

/**
 * Project-wide holiday calendar editor. Reads the singleton
 * `wbsConstraints/default` doc; saves use `updateDoc` so the rules
 * gate (editor+ via the existing `wbsConstraints` rules block) works
 * without needing to special-case create. New projects without an
 * imported sheet show a fallback message instead of bootstrapping the
 * doc from the client — bootstrap stays the seed function's job.
 */
function HolidaysSection({
  projectId,
  canEdit,
}: {
  projectId: string;
  canEdit: boolean;
}) {
  const { data: constraints, loading } = useWbsConstraints(projectId);
  // Add-form state.
  const [addName, setAddName] = useState('');
  const [addStart, setAddStart] = useState('');
  const [addEnd, setAddEnd] = useState('');
  // Edit-form state. `editingIdx` is the position within the SOURCE
  // `constraints.holidays` array — not the displayed (sorted) order.
  // Using the source index keeps writes deterministic even when two
  // entries share name+dates.
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editStart, setEditStart] = useState('');
  const [editEnd, setEditEnd] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  /**
   * Optimistic overlay. While a mutation is in flight (or has just
   * completed but the Firestore listener hasn't echoed yet), this
   * holds the post-write list so the user sees their change *now*
   * instead of waiting for the round-trip. Cleared as soon as the
   * listener emits — that emit is by definition the most-recent
   * authoritative state, so we hand back over to it.
   */
  const [optimistic, setOptimistic] = useState<WbsHoliday[] | null>(null);

  // Whenever the listener emits, the server-authoritative list takes
  // over. The check is intentionally cheap — we trust whatever the
  // listener gave us, even if it differs slightly from `optimistic`
  // (the listener's value is more recent by construction).
  useEffect(() => {
    if (constraints?.holidays !== undefined) setOptimistic(null);
  }, [constraints?.holidays]);

  // Source list = optimistic if present, else listener data. Every
  // downstream operation (sort, edit-by-index, delete-by-index) reads
  // from this so the views stay coherent during in-flight writes.
  const sourceList = optimistic ?? constraints?.holidays ?? [];

  // Display-order list, but each row carries its source index so the
  // editor knows which position to overwrite.
  const sorted = useMemo(() => {
    return sourceList
      .map((h, sourceIdx) => ({ h, sourceIdx }))
      .sort((a, b) => {
        const ax = tsToDate(a.h.start)?.getTime() ?? 0;
        const bx = tsToDate(b.h.start)?.getTime() ?? 0;
        return ax - bx;
      });
  }, [sourceList]);

  const fmt = (d: Date | null) => {
    if (!d) return '—';
    return `${d.getUTCFullYear()}/${String(d.getUTCMonth() + 1).padStart(2, '0')}/${String(d.getUTCDate()).padStart(2, '0')}`;
  };

  // Stored Timestamp → "YYYY-MM-DD" for <input type="date">. Accepts
  // the structural shared `Timestamp` so this works for both the web
  // SDK's class and the admin SDK's (writes always use FsTimestamp).
  const toDateInput = (ts: SharedTimestamp | null): string => {
    const d = tsToDate(ts);
    if (!d) return '';
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
  };

  // YYYY-MM-DD <input type="date"> string → UTC-midnight FsTimestamp.
  // The rest of the app labels days by their UTC date (`startOfDayUTC`),
  // so storing UTC midnight round-trips back to the same calendar day
  // via `toDateInput`'s `getUTC*` getters.
  const parseDateInput = (v: string): FsTimestamp | null => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
    if (!m) return null;
    const ms = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return FsTimestamp.fromMillis(ms);
  };

  const validate = (
    n: string,
    s: string,
    e: string,
  ): { name: string; start: FsTimestamp; end: FsTimestamp } | string => {
    if (!n.trim()) return '名前を入力してください';
    const sTs = parseDateInput(s);
    const eTs = parseDateInput(e || s);
    if (!sTs || !eTs) return '開始日を入力してください';
    if (eTs.toMillis() < sTs.toMillis()) {
      return '終了日は開始日以降の日付にしてください';
    }
    return { name: n.trim(), start: sTs, end: eTs };
  };

  const onAdd = async () => {
    setErr(null);
    const v = validate(addName, addStart, addEnd);
    if (typeof v === 'string') {
      setErr(v);
      return;
    }
    const next: WbsHoliday[] = [...sourceList, v];
    // Optimistically render the new row immediately; the listener will
    // either confirm (clears optimistic) or we revert on error.
    setOptimistic(next);
    setAddName('');
    setAddStart('');
    setAddEnd('');
    setBusy(true);
    try {
      await updateWbsConstraints(getDb(), projectId, { holidays: next });
    } catch (caught) {
      console.error('[settings] add holiday failed', caught);
      setOptimistic(null);
      setErr(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  };

  const beginEdit = (sourceIdx: number) => {
    const h = sourceList[sourceIdx];
    if (!h) return;
    setEditingIdx(sourceIdx);
    setEditName(h.name);
    setEditStart(toDateInput(h.start));
    setEditEnd(toDateInput(h.end));
    setErr(null);
  };

  const cancelEdit = () => {
    setEditingIdx(null);
    setErr(null);
  };

  const saveEdit = async () => {
    if (editingIdx == null) return;
    const v = validate(editName, editStart, editEnd);
    if (typeof v === 'string') {
      setErr(v);
      return;
    }
    const next = sourceList.map((h, i) => (i === editingIdx ? v : h));
    // Close the editor + flip the static row to the new values
    // *before* awaiting Firestore. Without this the user clicked save
    // and saw nothing change for the listener round-trip — making the
    // save look broken even when it had succeeded.
    setOptimistic(next);
    setEditingIdx(null);
    setBusy(true);
    try {
      await updateWbsConstraints(getDb(), projectId, { holidays: next });
    } catch (caught) {
      console.error('[settings] edit holiday failed', caught);
      setOptimistic(null);
      setErr(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (sourceIdx: number) => {
    const next = sourceList.filter((_, i) => i !== sourceIdx);
    setOptimistic(next);
    if (editingIdx === sourceIdx) setEditingIdx(null);
    setBusy(true);
    try {
      await updateWbsConstraints(getDb(), projectId, { holidays: next });
    } catch (caught) {
      console.error('[settings] delete holiday failed', caught);
      setOptimistic(null);
      setErr(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card p-4">
      <h2 className="text-lg font-semibold">休業日</h2>
      <p className="mt-1 text-xs text-slate-500">
        WBS / ガントの背景に黄色帯で表示します。連休や全社休業など、計画から外したい期間を登録してください。
      </p>

      {loading ? (
        <div className="mt-3 text-xs text-slate-500">読み込み中…</div>
      ) : !constraints ? (
        <div className="mt-3 rounded bg-slate-50 px-3 py-2 text-xs text-slate-500">
          まず WBS データを取り込むと、休業日の編集が可能になります。
        </div>
      ) : (
        <>
          <div className="mt-3 space-y-1.5">
            {sorted.length === 0 ? (
              <div className="text-xs text-slate-400">登録された休業日はありません</div>
            ) : (
              sorted.map(({ h, sourceIdx }) => {
                const isEditing = editingIdx === sourceIdx;
                if (isEditing && canEdit) {
                  return (
                    <div
                      key={`edit-${sourceIdx}`}
                      className="rounded border border-sky-300 bg-sky-50/50 px-3 py-2"
                    >
                      <div className="grid grid-cols-[1fr_auto_auto] items-end gap-2">
                        <div>
                          <Label>名前</Label>
                          <Input
                            value={editName}
                            onChange={(ev) => setEditName(ev.target.value)}
                          />
                        </div>
                        <div>
                          <Label>開始</Label>
                          <DateInput
                            value={editStart}
                            onChange={(next) => setEditStart(next)}
                          />
                        </div>
                        <div>
                          <Label>終了</Label>
                          <DateInput
                            value={editEnd}
                            onChange={(next) => setEditEnd(next)}
                          />
                        </div>
                      </div>
                      <div className="mt-2 flex items-center justify-end gap-2">
                        <Button
                          variant="secondary"
                          disabled={busy}
                          onClick={cancelEdit}
                        >
                          キャンセル
                        </Button>
                        <Button disabled={busy} onClick={saveEdit}>
                          保存
                        </Button>
                      </div>
                      {err ? (
                        <p className="mt-2 text-xs text-red-600">{err}</p>
                      ) : null}
                    </div>
                  );
                }
                const s = tsToDate(h.start);
                const e = tsToDate(h.end);
                const sameDay =
                  s && e && s.getTime() === e.getTime();
                return (
                  <div
                    key={`${sourceIdx}-${h.name}-${h.start.toMillis()}`}
                    className={cn(
                      'flex items-center justify-between rounded border border-slate-200 px-3 py-1.5 text-sm',
                      canEdit ? 'cursor-pointer hover:bg-slate-50' : '',
                    )}
                    onClick={canEdit ? () => beginEdit(sourceIdx) : undefined}
                  >
                    <div className="min-w-0">
                      <div className="truncate font-medium">{h.name}</div>
                      <div className="text-xs text-slate-500">
                        {sameDay ? fmt(s) : `${fmt(s)} – ${fmt(e)}`}
                      </div>
                    </div>
                    {canEdit ? (
                      <Button
                        variant="ghost"
                        className="!px-2"
                        aria-label="削除"
                        disabled={busy}
                        onClick={(ev) => {
                          ev.stopPropagation();
                          void onDelete(sourceIdx);
                        }}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    ) : null}
                  </div>
                );
              })
            )}
          </div>

          {canEdit ? (
            <div className="mt-4 border-t border-slate-200 pt-4">
              <h3 className="text-sm font-semibold">休業日を追加</h3>
              <div className="mt-2 grid grid-cols-[1fr_auto_auto_auto] items-end gap-2">
                <div>
                  <Label>名前</Label>
                  <Input
                    value={addName}
                    onChange={(ev) => setAddName(ev.target.value)}
                    placeholder="GW / 年末年始 / 全社休業 など"
                  />
                </div>
                <div>
                  <Label>開始</Label>
                  <DateInput
                    value={addStart}
                    onChange={(next) => setAddStart(next)}
                  />
                </div>
                <div>
                  <Label>終了</Label>
                  <DateInput
                    value={addEnd}
                    onChange={(next) => setAddEnd(next)}
                    placeholder="(同日でOK)"
                  />
                </div>
                <Button
                  disabled={busy || !addName.trim() || !addStart}
                  onClick={onAdd}
                >
                  追加
                </Button>
              </div>
              {err && editingIdx == null ? (
                <p className="mt-2 text-xs text-red-600">{err}</p>
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}

/* ----------------------------------------------------------------------- */
/*  Assignee list + user-mapping editor                                     */
/* ----------------------------------------------------------------------- */

/**
 * Two-pane editor:
 *   1. The project's assignee list (`/projects/{pid}/assignees`). Each
 *      row renders the same colored chip that WBS / migration tasks
 *      use; clicking the chip opens the **same** `CatalogOptionsEditor`
 *      dialog that the WBS page uses for 領域 / ベンダー / 担当, so
 *      add / rename / recolour / delete-with-guard share one component.
 *   2. A mapping table from each assignee to a real Firebase Auth user
 *      (`/projects/{pid}/assigneeUserMap`). 1:1 enforcement is dual:
 *      the helper queries before write, and the rules-side guard freezes
 *      `userId` once bound. The picker is a custom popover (not a
 *      native `<select>`) so it can stay open while the save is in
 *      flight, surface success / error inline, and roll the row back on
 *      failure instead of silently dismissing on the user's click.
 *
 * Visibility: viewers see the table read-only; editor+ may add / rename
 * assignees; only owner/admin may bind a real user (matches the rules).
 */

/** One staged auto-match suggestion awaiting the admin's confirmation. */
interface AutoMatchProposal {
  assigneeId: string;
  assigneeName: string;
  backlogUserId: number;
  backlogUserName: string;
  matchType: 'exact' | 'prefix' | 'prefix-ambiguous';
  /** Whether this row is ticked for the bulk apply. */
  accepted: boolean;
}

function AssigneeMappingSection({
  project,
  canEdit,
}: {
  project: ProjectDoc;
  canEdit: boolean;
}) {
  const { user } = useAuthState();
  const { data: assignees, loading: aLoading } = useAssignees(project.projectId);
  const { data: mappings, loading: mLoading } = useAssigneeUserMappings(
    project.projectId,
  );
  // Load WBS items + migration tasks so the delete-guard can show a
  // usage count. These hooks are already cached / live-updated, so the
  // extra subscriptions are cheap and stay in sync with the rest of the
  // app. If the project doc isn't ready yet `useWbsItems`/`useTasks`
  // gracefully return [] until the listener attaches.
  const { data: wbsItemsForUsage } = useWbsItems(project.projectId);
  const { data: tasksForUsage } = useTasks(project.projectId);

  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  /** True when the WBS-shared CatalogOptionsEditor dialog is open. */
  const [editorOpen, setEditorOpen] = useState(false);

  // Backlog user roster — lazily fetched from `listAllBacklogUsers` the
  // first time a picker opens (or auto-match runs). `null` = not loaded.
  const [backlogUsers, setBacklogUsers] = useState<BacklogUserListItem[] | null>(
    null,
  );
  const [backlogUsersLoading, setBacklogUsersLoading] = useState(false);
  const [backlogUsersError, setBacklogUsersError] = useState<string | null>(null);
  /** Auto-match review state: proposals awaiting the admin's confirmation. */
  const [autoMatchProposals, setAutoMatchProposals] = useState<
    AutoMatchProposal[] | null
  >(null);
  const [autoMatchBusy, setAutoMatchBusy] = useState(false);
  /** True when the "Backlog ユーザーから担当者を追加" modal is open. */
  const [addFromBacklogOpen, setAddFromBacklogOpen] = useState(false);
  const [addFromBacklogBusy, setAddFromBacklogBusy] = useState(false);

  /** Map of `assigneeId -> usage count` across WBS rows and migration tasks. */
  const assigneeUsage = useMemo<Map<string, number>>(() => {
    const byId = new Map<string, number>();
    for (const it of wbsItemsForUsage) {
      if (it.assigneeId) byId.set(it.assigneeId, (byId.get(it.assigneeId) ?? 0) + 1);
    }
    for (const t of tasksForUsage) {
      if (t.assigneeId) byId.set(t.assigneeId, (byId.get(t.assigneeId) ?? 0) + 1);
    }
    const out = new Map<string, number>();
    for (const a of assignees) {
      out.set(a.assigneeId, byId.get(a.assigneeId) ?? 0);
    }
    return out;
  }, [assignees, wbsItemsForUsage, tasksForUsage]);

  /**
   * The shared CatalogOptionsEditor uses *label* keys for its in-use
   * snapshot (the editor doesn't know about Firestore ids), so we
   * project the id-keyed usage map down to label-keyed before passing
   * it in. Duplicate names still produce a defensible total — same
   * behaviour as the WBS page's vendor / area editors.
   */
  const assigneeUsageByLabel = useMemo<Map<string, number>>(() => {
    const m = new Map<string, number>();
    for (const a of assignees) {
      const n = assigneeUsage.get(a.assigneeId) ?? 0;
      m.set(a.name, (m.get(a.name) ?? 0) + n);
    }
    return m;
  }, [assignees, assigneeUsage]);

  /**
   * Catalog form for the shared editor: id is the assigneeId, label is
   * the display name, colorIndex is the frozen palette slot. Identical
   * to what `WbsPage` builds, so the two surfaces share both the
   * editor component and the save semantics.
   */
  const assigneeCatalogOptions = useMemo(
    () =>
      assignees.map((a) => ({
        id: a.assigneeId,
        label: a.name,
        colorIndex: a.colorIndex,
      })),
    [assignees],
  );

  // Fast lookup: assigneeId -> userId. Used by the rows so each picker
  // can show the current binding without scanning the whole list.
  const userByAssigneeId = useMemo(() => {
    const m = new Map<string, string>();
    // Backlog-only mappings carry no Migratus userId — skip them here.
    for (const r of mappings) if (r.userId) m.set(r.assigneeId, r.userId);
    return m;
  }, [mappings]);

  // Inverse lookup: userId -> assigneeId. Used by the per-row picker to
  // grey out users already bound to *another* assignee — the 1:1
  // constraint is also enforced server-side, but visual feedback makes
  // the rule legible before the click.
  const assigneeByUserId = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of mappings) if (r.userId) m.set(r.userId, r.assigneeId);
    return m;
  }, [mappings]);

  // assigneeId -> linked Backlog user ({ id, name }). Drives each row's
  // Backlog picker label and the "already linked" filter for auto-match.
  const backlogByAssigneeId = useMemo(
    () => indexBacklogByAssigneeId(mappings),
    [mappings],
  );

  // Inverse: backlogUserId -> assigneeId, so the picker can grey out a
  // Backlog user already bound to another assignee (1:1 visual feedback).
  const assigneeByBacklogUserId = useMemo(() => {
    const m = new Map<number, string>();
    for (const r of mappings) {
      if (r.backlogUserId != null) m.set(r.backlogUserId, r.assigneeId);
    }
    return m;
  }, [mappings]);

  const backlogUserById = useMemo(() => {
    const m = new Map<number, BacklogUserListItem>();
    for (const u of backlogUsers ?? []) m.set(u.id, u);
    return m;
  }, [backlogUsers]);

  /**
   * Lazily fetch the Backlog user roster. Cached after the first call;
   * pass `force` to re-fetch (the picker's "再取得" and auto-match use it).
   * Returns the users on success or `null` on failure (error is surfaced
   * via `backlogUsersError`).
   */
  const ensureBacklogUsers = async (
    force = false,
  ): Promise<BacklogUserListItem[] | null> => {
    if (backlogUsers && !force) return backlogUsers;
    setBacklogUsersLoading(true);
    setBacklogUsersError(null);
    try {
      const res = await listAllBacklogUsers(project.projectId);
      if (!res.ok) {
        setBacklogUsersError(res.message);
        return null;
      }
      setBacklogUsers(res.users);
      return res.users;
    } catch (e) {
      setBacklogUsersError(translateCallableError(e).message);
      return null;
    } finally {
      setBacklogUsersLoading(false);
    }
  };

  const onAdd = async () => {
    if (!newName.trim()) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      // Auto-assign the next free palette slot. Existing assignees'
      // frozen colorIndex values seed the "taken" set so we don't
      // collide on add. When all slots are taken `pickNextFreeColorIndex`
      // wraps to 0 — the user can always recolour after the fact.
      const colorIndex = pickNextFreeColorIndex(
        assignees.map((a) => a.colorIndex),
      );
      const id = await createAssignee(getDb(), project, {
        name: newName,
        colorIndex,
      });
      setNewName('');
      setInfo(`担当者「${newName.trim()}」を追加しました (${id})`);
    } catch (e) {
      setError(e instanceof Error ? e.message : '追加に失敗しました');
    } finally {
      setBusy(false);
    }
  };

  /**
   * Save handler for the shared CatalogOptionsEditor. Identical
   * behaviour to the WBS page's 担当 editor — both call into
   * `applyAssigneeCatalogEdit` so a rename or recolour kicked off from
   * either surface produces the same Firestore writes.
   */
  const onSaveCatalog = async (result: CatalogOptionEditResult) => {
    setError(null);
    setInfo(null);
    try {
      await applyAssigneeCatalogEdit(
        getDb(),
        project,
        assignees,
        assigneeUsage,
        result,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : '保存に失敗しました');
      throw e;
    }
  };

  /**
   * Bind / unbind a real user. Returns the popover-friendly result
   * shape (`{ ok: true } | { ok: false, error }`) so the picker can
   * decide whether to close itself or stay open and surface the
   * message inline. We do *not* optimistically commit anywhere — the
   * `useAssigneeUserMappings` listener emits the canonical state on
   * success, and the popover's per-row UI is what visibly rolls back
   * on failure (it never showed the new value to begin with).
   */
  const onBindUser = async (
    assigneeId: string,
    nextUserId: string | null,
  ): Promise<UserMappingPickApiResult> => {
    if (!user) return { ok: false, error: 'サインインが必要です' };
    try {
      // The live `useAssigneeUserMappings` listener is the source of
      // truth for "is there already a mapping doc for this assignee?".
      // Passing a boolean derived from it lets the helper skip the
      // pre-flight `getDoc(ref)` that previously triggered
      // permission-denied on first bind: the read rule for
      // `assigneeUserMap` references `resource.data.memberUserIds`,
      // which is null for non-existent docs, so the SDK probe was
      // rejected before the actual create even ran.
      const priorMappingExists = userByAssigneeId.has(assigneeId);
      const result = await setAssigneeUserMapping(
        getDb(),
        project,
        assigneeId,
        nextUserId,
        user.uid,
        priorMappingExists,
      );
      if (result.ok) return { ok: true };
      if (result.reason === 'duplicate') {
        const otherName =
          assignees.find((a) => a.assigneeId === result.existingAssigneeId)
            ?.name ?? result.existingAssigneeId;
        return {
          ok: false,
          error: `そのユーザーは既に担当者「${otherName}」に紐付け済みです。先に解除してください。`,
        };
      }
      return { ok: false, error: '紐付けに失敗しました' };
    } catch (e) {
      // Surface a Japanese, action-oriented message for the most
      // common failure ("permission-denied" → admin-only). Other
      // errors fall through verbatim so we don't mask transport
      // issues. See `localizeAssigneeMappingError` for the mapping.
      return {
        ok: false,
        error: localizeAssigneeMappingError(e),
      };
    }
  };

  /**
   * Link / relink / unlink the Backlog user for one assignee. Mirrors
   * `onBindUser` but writes the `backlogUserId` / `backlogUserName` side
   * of the shared mapping doc via `setAssigneeBacklogUser`.
   */
  const onBindBacklog = async (
    assigneeId: string,
    backlog: BacklogUserLink | null,
  ): Promise<UserMappingPickApiResult> => {
    if (!user) return { ok: false, error: 'サインインが必要です' };
    try {
      const m = mappings.find((x) => x.assigneeId === assigneeId);
      const result = await setAssigneeBacklogUser(
        getDb(),
        project,
        assigneeId,
        backlog,
        user.uid,
        Boolean(m),
        Boolean(m?.userId),
      );
      if (result.ok) return { ok: true };
      if (result.reason === 'duplicate') {
        const otherName =
          assignees.find((a) => a.assigneeId === result.existingAssigneeId)
            ?.name ?? result.existingAssigneeId;
        return {
          ok: false,
          error: `その Backlog ユーザーは既に担当者「${otherName}」に紐付け済みです。先に解除してください。`,
        };
      }
      return { ok: false, error: '紐付けに失敗しました' };
    } catch (e) {
      return { ok: false, error: localizeAssigneeMappingError(e) };
    }
  };

  /**
   * Propose Backlog-user links for every still-unlinked assignee using the
   * shared fuzzy matcher (whitespace-stripped exact, then surname-prefix).
   * Results are staged in `autoMatchProposals` for the admin to review and
   * confirm — nothing is written until they click 適用.
   */
  const onAutoMatch = async () => {
    setError(null);
    setInfo(null);
    setAutoMatchBusy(true);
    try {
      const users = await ensureBacklogUsers(true);
      if (!users) {
        setError(backlogUsersError ?? 'Backlog ユーザーを取得できませんでした');
        return;
      }
      const raw = proposeBacklogAutoMatches({
        assignees: assignees.map((a) => ({
          assigneeId: a.assigneeId,
          name: a.name,
        })),
        backlogUsers: users,
        linkedAssigneeIds: new Set(backlogByAssigneeId.keys()),
        linkedBacklogUserIds: new Set(assigneeByBacklogUserId.keys()),
      });
      if (raw.length === 0) {
        setInfo('自動マッチできる候補は見つかりませんでした。');
        setAutoMatchProposals(null);
        return;
      }
      // Ambiguous prefix matches start unticked so the admin opts in.
      setAutoMatchProposals(
        raw.map((p) => ({
          ...p,
          accepted: p.matchType !== 'prefix-ambiguous',
        })),
      );
    } finally {
      setAutoMatchBusy(false);
    }
  };

  /** Bulk-apply the accepted auto-match proposals, then clear the panel. */
  const applyAutoMatch = async () => {
    if (!autoMatchProposals) return;
    const accepted = autoMatchProposals.filter((p) => p.accepted);
    if (accepted.length === 0) {
      setAutoMatchProposals(null);
      return;
    }
    setAutoMatchBusy(true);
    setError(null);
    setInfo(null);
    let ok = 0;
    let failed = 0;
    for (const p of accepted) {
      const res = await onBindBacklog(p.assigneeId, {
        id: p.backlogUserId,
        name: p.backlogUserName,
      });
      if (res.ok) ok += 1;
      else failed += 1;
    }
    setAutoMatchBusy(false);
    setAutoMatchProposals(null);
    setInfo(
      `Backlog ユーザーを ${ok} 件紐付けました${failed > 0 ? `（${failed} 件失敗）` : ''}。`,
    );
  };

  /**
   * Open the "add assignee from Backlog user" modal, lazy-loading the
   * roster first so the list is populated when it appears.
   */
  const openAddFromBacklog = async () => {
    setError(null);
    setInfo(null);
    setAddFromBacklogOpen(true);
    await ensureBacklogUsers();
  };

  /** Bulk-create assignees (+ Backlog link) for the selected Backlog users. */
  const onAddAssigneesFromBacklog = async (selected: BacklogUserListItem[]) => {
    if (!user || selected.length === 0) return;
    setAddFromBacklogBusy(true);
    setError(null);
    setInfo(null);
    let ok = 0;
    let failed = 0;
    // Seed the palette with existing slots so new rows don't collide; bump
    // the seed as we go so a batch of creates spreads across colors.
    const taken = assignees.map((a) => a.colorIndex);
    try {
      for (const bu of selected) {
        try {
          const colorIndex = pickNextFreeColorIndex(taken);
          taken.push(colorIndex);
          await createAssigneeWithBacklogUser(
            getDb(),
            project,
            { id: bu.id, name: bu.name },
            user.uid,
            colorIndex,
          );
          ok += 1;
        } catch {
          failed += 1;
        }
      }
    } finally {
      setAddFromBacklogBusy(false);
      setAddFromBacklogOpen(false);
      setInfo(
        `Backlog ユーザーから担当者を ${ok} 件追加しました${failed > 0 ? `（${failed} 件失敗）` : ''}。`,
      );
    }
  };

  const memberOptions = Object.entries(project.members).map(([uid, m]) => ({
    uid,
    label: m.displayName || m.email,
  }));

  return (
    <section className="card p-4">
      <h2 className="text-lg font-semibold">担当者リスト & ユーザー紐付け</h2>
      <p className="mt-1 text-xs text-slate-500">
        WBS と移行作業（タスク）で共通利用する担当者ラベルです。実ユーザー（メンバー）と
        紐付けると「自分の担当」フィルタなどから参照できます。同じユーザーを複数の担当者に
        紐付けることはできません（1:1）。名前・色の編集は右上の「担当者リストを編集」から
        行ってください。
      </p>

      {!canEdit ? (
        <p className="mt-1 text-xs text-slate-500">
          編集にはオーナー / 管理者権限が必要です。
        </p>
      ) : null}

      {error ? (
        <p className="mt-2 rounded border border-red-300 bg-red-50 px-2 py-1 text-xs text-red-700">
          {error}
        </p>
      ) : null}
      {info ? (
        <p className="mt-2 rounded border border-emerald-300 bg-emerald-50 px-2 py-1 text-xs text-emerald-800">
          {info}
        </p>
      ) : null}

      {aLoading || mLoading ? (
        <div className="mt-4 flex justify-center">
          <Spinner />
        </div>
      ) : (
        <>
          {canEdit ? (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Button
                variant="secondary"
                disabled={autoMatchBusy || assignees.length === 0}
                onClick={() => void onAutoMatch()}
                data-testid="backlog-auto-match-button"
              >
                {autoMatchBusy ? (
                  <span className="inline-flex items-center gap-1">
                    <Spinner className="h-3 w-3" />
                    照合中…
                  </span>
                ) : (
                  'Backlog ユーザーを自動マッチ'
                )}
              </Button>
              <Button
                variant="secondary"
                disabled={autoMatchBusy}
                onClick={() => void openAddFromBacklog()}
                data-testid="backlog-add-assignee-button"
              >
                Backlog ユーザーから担当者を追加
              </Button>
              <span className="text-[11px] text-slate-500">
                名前の揺らぎを吸収して未連携の担当者に Backlog ユーザー候補を提案します。
              </span>
            </div>
          ) : null}

          {addFromBacklogOpen ? (
            <BacklogUserAddModal
              users={backlogUsers}
              loading={backlogUsersLoading}
              loadError={backlogUsersError}
              busy={addFromBacklogBusy}
              alreadyLinkedBacklogIds={assigneeByBacklogUserId}
              existingAssigneeNames={assignees.map((a) => a.name)}
              onAdd={onAddAssigneesFromBacklog}
              onClose={() => setAddFromBacklogOpen(false)}
            />
          ) : null}

          {backlogUsersError ? (
            <p className="mt-2 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-800">
              Backlog ユーザーの取得に失敗しました: {backlogUsersError}
            </p>
          ) : null}

          {autoMatchProposals ? (
            <BacklogAutoMatchPanel
              proposals={autoMatchProposals}
              busy={autoMatchBusy}
              onToggle={(i) =>
                setAutoMatchProposals((prev) =>
                  prev
                    ? prev.map((p, idx) =>
                        idx === i ? { ...p, accepted: !p.accepted } : p,
                      )
                    : prev,
                )
              }
              onApply={() => void applyAutoMatch()}
              onCancel={() => setAutoMatchProposals(null)}
            />
          ) : null}

          <div className="relative mt-4 overflow-visible rounded border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-600">
                <tr>
                  <th className="px-3 py-2 font-medium">担当者</th>
                  <th className="px-3 py-2 font-medium">紐付けユーザー</th>
                  <th className="px-3 py-2 font-medium">Backlog ユーザー</th>
                  <th className="px-3 py-2 text-right font-medium">
                    {canEdit ? (
                      <button
                        type="button"
                        onClick={() => setEditorOpen(true)}
                        className="rounded border border-slate-300 bg-white px-2 py-0.5 text-[11px] font-medium normal-case text-slate-700 hover:bg-slate-100"
                        data-testid="assignee-catalog-edit-button"
                      >
                        担当者リストを編集
                      </button>
                    ) : null}
                  </th>
                </tr>
              </thead>
              <tbody>
                {assignees.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-3 py-4 text-center text-xs text-slate-500"
                    >
                      担当者リストは空です。下のフォームから追加してください。
                    </td>
                  </tr>
                ) : (
                  assignees.map((a) => {
                    const boundUid = userByAssigneeId.get(a.assigneeId) ?? null;
                    return (
                      <tr
                        key={a.assigneeId}
                        className="border-t border-slate-100 hover:bg-slate-50"
                      >
                        <td className="px-3 py-2 align-middle">
                          {/* The chip in this section is *decoration* —
                              clicking it must NOT open an editor (the
                              編集 button in the header is the single
                              entrypoint for renaming / recolouring).
                              See bug 1 in the assignee-mapping-fixes
                              brief: a chip-as-button surprised users
                              by opening the WBS-shared dialog from a
                              context that's all about user binding. */}
                          <AssigneeChip assignee={a} title={a.name} />
                        </td>
                        <td className="px-3 py-2 align-middle">
                          {canEdit ? (
                            <UserMappingPicker
                              assigneeId={a.assigneeId}
                              currentUid={boundUid}
                              members={memberOptions}
                              isUidTakenByOther={(uid) => {
                                const otherId = assigneeByUserId.get(uid);
                                return (
                                  otherId !== undefined &&
                                  otherId !== a.assigneeId
                                );
                              }}
                              memberLabelByUid={(uid) =>
                                project.members[uid]?.displayName ??
                                project.members[uid]?.email ??
                                uid
                              }
                              onPick={(uid) => onBindUser(a.assigneeId, uid)}
                            />
                          ) : (
                            // Read-only view for members who lack
                            // owner / admin / platform-admin rights.
                            // The dropdown is intentionally NOT
                            // rendered here — clicking a control
                            // they can't act on is a worse failure
                            // mode than just showing the current
                            // binding as plain text. See the
                            // assignee-mapping-fixes brief:
                            // "権限がない人には dropdown 自体を表示しない".
                            <AssigneeUserMappingReadOnlyCell
                              memberLabel={
                                boundUid
                                  ? (project.members[boundUid]?.displayName ?? boundUid)
                                  : null
                              }
                            />
                          )}
                        </td>
                        <td className="px-3 py-2 align-middle">
                          {(() => {
                            const link =
                              backlogByAssigneeId.get(a.assigneeId) ?? null;
                            if (canEdit) {
                              return (
                                <BacklogUserPicker
                                  current={link}
                                  users={backlogUsers}
                                  loading={backlogUsersLoading}
                                  loadError={backlogUsersError}
                                  isUserTakenByOther={(id) => {
                                    const otherId =
                                      assigneeByBacklogUserId.get(id);
                                    return (
                                      otherId !== undefined &&
                                      otherId !== a.assigneeId
                                    );
                                  }}
                                  onOpen={() => void ensureBacklogUsers()}
                                  onRefresh={() => void ensureBacklogUsers(true)}
                                  onPick={(bu) =>
                                    onBindBacklog(a.assigneeId, bu)
                                  }
                                />
                              );
                            }
                            // Read-only: just show the linked name (or 未連携).
                            return (
                              <span
                                data-testid="assignee-backlog-readonly"
                                className={cn(
                                  'inline-flex items-center text-xs',
                                  link ? 'text-slate-600' : 'text-slate-400',
                                )}
                              >
                                {link
                                  ? `${
                                      backlogUserById.get(link.id)?.name ??
                                      link.name ??
                                      'Backlog ユーザー'
                                    } (${link.id})`
                                  : '— 未連携 —'}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-3 py-2 align-middle text-right text-xs text-slate-400">
                          {/* Right-most column is reserved for the
                              「担当者リストを編集」trigger in the header.
                              Per-row delete / rename moved into the
                              shared editor dialog so the Settings page
                              and the WBS page share one editing
                              surface. */}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
            {canEdit && editorOpen ? (
              <div className="absolute right-2 top-10 z-40">
                <CatalogOptionsEditor
                  title="担当者リストを編集"
                  noun="担当"
                  options={assigneeCatalogOptions}
                  usageCounts={assigneeUsageByLabel}
                  onClose={() => setEditorOpen(false)}
                  onSave={onSaveCatalog}
                />
              </div>
            ) : null}
          </div>

          {canEdit ? (
            <div className="mt-4 flex items-end gap-3">
              <div className="flex-1">
                <Label htmlFor="new-assignee-name">新しい担当者名</Label>
                <Input
                  id="new-assignee-name"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="例: 佐藤 太郎、横河SS（外注）"
                />
              </div>
              <Button
                disabled={busy || !newName.trim()}
                onClick={() => void onAdd()}
              >
                追加
              </Button>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}

/**
 * Read-only stand-in for the user-binding dropdown. Rendered to
 * members (editor / viewer / non-platform-admin) who can read the
 * mapping but cannot mutate it — the actual `<UserMappingPicker>` is
 * skipped entirely so a click on the row produces no half-broken
 * dropdown. Exposed at module scope so tests can render it directly
 * with `react-dom/server` instead of having to mount the whole
 * AssigneeMappingSection (which depends on Firestore hooks).
 *
 * The tooltip ("この設定は管理者のみ変更できます") gives the user a
 * one-line nudge to ask an admin without flooding the page with
 * permission banners on every row.
 */
export function AssigneeUserMappingReadOnlyCell({
  memberLabel,
}: {
  /** Bound user's display label. `null` renders the "未紐付け" placeholder. */
  memberLabel: string | null;
}): JSX.Element {
  return (
    <span
      data-testid="assignee-user-mapping-readonly"
      title="この設定は管理者のみ変更できます"
      className={cn(
        'inline-flex items-center text-xs text-slate-400',
        memberLabel && 'text-slate-500',
      )}
    >
      {memberLabel ?? '— 未紐付け —'}
    </span>
  );
}

/**
 * Custom popover for picking the real Firebase user bound to an
 * assignee. Replaces the previous native `<select>`, which had two
 * UX bugs:
 *
 *   1. Selecting an option *immediately* dismissed the dropdown
 *      (browser default), so the user had no signal as to whether the
 *      Firestore write was in flight, succeeded, or failed.
 *   2. On failure the row still rendered the would-be new value for a
 *      tick because `<select>` updates synchronously before the
 *      onSnapshot listener echoes the rejection.
 *
 * This popover stays open across the await: it shows a spinner while
 * the save runs, closes on success (the onSnapshot listener takes
 * over — no optimistic commit needed), and on failure stays open with
 * the error inline. The list visibly rolls back because we never
 * showed the failed value in the first place.
 *
 * Outside-click closes the popover, but only when the save isn't in
 * flight — clicking outside mid-save would otherwise hide the spinner
 * and leave the user staring at a still-rendered old value.
 */
function UserMappingPicker({
  assigneeId,
  currentUid,
  members,
  isUidTakenByOther,
  memberLabelByUid,
  onPick,
}: {
  assigneeId: string;
  currentUid: string | null;
  members: { uid: string; label: string }[];
  isUidTakenByOther: (uid: string) => boolean;
  memberLabelByUid: (uid: string) => string;
  onPick: (uid: string | null) => Promise<UserMappingPickApiResult>;
}) {
  void assigneeId; // reserved for future per-row diagnostics; included
                   // so the prop list mirrors the row's data shape.
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  // Outside-click closes the popover — but only when no save is in
  // flight. Clicking outside mid-save would hide the spinner and
  // leave the user with no feedback.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (saving) return;
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setErr(null);
      }
    };
    const t = window.setTimeout(() => {
      document.addEventListener('mousedown', onDown);
    }, 0);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open, saving]);

  // Esc closes when nothing is in flight.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) {
        setOpen(false);
        setErr(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, saving]);

  const handlePick = async (uid: string | null) => {
    if (saving) return;
    await runUserMappingPick(() => onPick(uid), {
      onStart: () => {
        setSaving(true);
        setErr(null);
      },
      onSuccess: () => {
        setSaving(false);
        setOpen(false);
        setShowSuccess(true);
        window.setTimeout(() => setShowSuccess(false), 1800);
      },
      onError: (message) => {
        setSaving(false);
        setErr(message);
      },
    });
  };

  const currentLabel =
    currentUid != null
      ? memberLabelByUid(currentUid)
      : '— 未紐付け —';

  return (
    <div className="relative" ref={ref} data-testid="user-mapping-picker">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'inline-flex w-full max-w-[18rem] items-center justify-between gap-1 rounded border border-slate-200 bg-white px-2 py-0.5 text-xs hover:border-slate-400',
        )}
        data-testid="user-mapping-trigger"
      >
        <span
          className={cn(
            'truncate',
            currentUid == null ? 'text-slate-400' : '',
          )}
        >
          {currentLabel}
        </span>
        {saving ? (
          <Spinner className="h-3 w-3" />
        ) : showSuccess ? (
          <Check
            className="h-3 w-3 text-emerald-600"
            data-testid="user-mapping-success"
          />
        ) : null}
      </button>
      {open ? (
        <div
          className="absolute left-0 top-full z-30 mt-1 w-64 rounded-md border border-slate-200 bg-white py-1 text-xs shadow-lg"
          role="listbox"
          data-testid="user-mapping-popover"
        >
          <button
            type="button"
            disabled={saving}
            onClick={() => void handlePick(null)}
            className={cn(
              'block w-full px-3 py-1 text-left hover:bg-slate-50 disabled:cursor-wait disabled:opacity-60',
              currentUid == null ? 'bg-slate-50 font-medium' : '',
            )}
          >
            — 未紐付け —
          </button>
          {members.map((m) => {
            const taken = isUidTakenByOther(m.uid);
            const isCurrent = m.uid === currentUid;
            return (
              <button
                key={m.uid}
                type="button"
                disabled={saving || (taken && !isCurrent)}
                onClick={() => void handlePick(m.uid)}
                className={cn(
                  'block w-full px-3 py-1 text-left hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50',
                  isCurrent ? 'bg-slate-50 font-medium' : '',
                )}
                data-testid={`user-mapping-option-${m.uid}`}
              >
                {m.label}
                {taken && !isCurrent ? '（他の担当者に紐付け済）' : ''}
              </button>
            );
          })}
          {saving ? (
            <div
              className="flex items-center gap-1 border-t border-slate-100 px-3 py-1 text-[10px] text-slate-500"
              data-testid="user-mapping-saving"
            >
              <Spinner className="h-3 w-3" />
              <span>保存中…</span>
            </div>
          ) : null}
          {err ? (
            <div
              className="border-t border-red-100 bg-red-50 px-3 py-1 text-[10px] text-red-700"
              data-testid="user-mapping-error"
            >
              {err}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Per-row Backlog-user picker. Sibling of {@link UserMappingPicker} but
 * for the `backlogUserId` link. Differences:
 *
 *   - The roster is fetched lazily from `listAllBacklogUsers` (the parent
 *     passes it down once loaded); opening the popover triggers `onOpen`.
 *   - A search box filters the (potentially long) Backlog roster by name.
 *   - The "再取得" action re-fetches the roster so a Backlog-side rename
 *     can be pulled in on demand (we never auto-sync the denormalized name).
 *
 * Same stay-open-across-the-await UX so the save's spinner / error are
 * visible and the row never shows an un-committed value.
 */
export function BacklogUserPicker({
  current,
  users,
  loading,
  loadError,
  isUserTakenByOther,
  onOpen,
  onRefresh,
  onPick,
}: {
  current: { id: number; name: string | null } | null;
  users: BacklogUserListItem[] | null;
  loading: boolean;
  loadError: string | null;
  isUserTakenByOther: (id: number) => boolean;
  onOpen: () => void;
  onRefresh: () => void;
  onPick: (backlog: BacklogUserLink | null) => Promise<UserMappingPickApiResult>;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (saving) return;
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setErr(null);
      }
    };
    const t = window.setTimeout(() => {
      document.addEventListener('mousedown', onDown);
    }, 0);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open, saving]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) {
        setOpen(false);
        setErr(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, saving]);

  const handlePick = async (backlog: BacklogUserLink | null) => {
    if (saving) return;
    await runUserMappingPick(() => onPick(backlog), {
      onStart: () => {
        setSaving(true);
        setErr(null);
      },
      onSuccess: () => {
        setSaving(false);
        setOpen(false);
        setShowSuccess(true);
        window.setTimeout(() => setShowSuccess(false), 1800);
      },
      onError: (message) => {
        setSaving(false);
        setErr(message);
      },
    });
  };

  const toggleOpen = () => {
    setOpen((v) => {
      const next = !v;
      if (next) onOpen();
      return next;
    });
  };

  const currentLabel = current
    ? `${current.name ?? 'Backlog ユーザー'} (${current.id})`
    : '— 未連携 —';

  const needle = q.trim().toLowerCase();
  const filtered = (users ?? []).filter(
    (u) =>
      needle === '' ||
      u.name.toLowerCase().includes(needle) ||
      String(u.id).includes(needle),
  );

  return (
    <div className="relative" ref={ref} data-testid="backlog-user-picker">
      <button
        type="button"
        onClick={toggleOpen}
        className="inline-flex w-full max-w-[18rem] items-center justify-between gap-1 rounded border border-slate-200 bg-white px-2 py-0.5 text-xs hover:border-slate-400"
        data-testid="backlog-user-trigger"
      >
        <span className={cn('truncate', current == null ? 'text-slate-400' : '')}>
          {currentLabel}
        </span>
        {saving ? (
          <Spinner className="h-3 w-3" />
        ) : showSuccess ? (
          <Check
            className="h-3 w-3 text-emerald-600"
            data-testid="backlog-user-success"
          />
        ) : null}
      </button>
      {open ? (
        <div
          className="absolute left-0 top-full z-30 mt-1 w-72 rounded-md border border-slate-200 bg-white py-1 text-xs shadow-lg"
          role="listbox"
          data-testid="backlog-user-popover"
        >
          <div className="flex items-center gap-1 px-2 pb-1">
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Backlog ユーザーを検索…"
              className="h-7 text-xs"
              data-testid="backlog-user-search"
            />
            <button
              type="button"
              onClick={onRefresh}
              disabled={loading}
              title="Backlog から再取得"
              className="shrink-0 rounded border border-slate-200 px-1.5 py-1 text-[10px] text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              data-testid="backlog-user-refresh"
            >
              再取得
            </button>
          </div>
          <button
            type="button"
            disabled={saving}
            onClick={() => void handlePick(null)}
            className={cn(
              'block w-full px-3 py-1 text-left hover:bg-slate-50 disabled:cursor-wait disabled:opacity-60',
              current == null ? 'bg-slate-50 font-medium' : '',
            )}
          >
            — 未連携 —
          </button>
          <div className="max-h-56 overflow-auto">
            {loading ? (
              <div className="flex items-center gap-1 px-3 py-2 text-[10px] text-slate-500">
                <Spinner className="h-3 w-3" />
                <span>読み込み中…</span>
              </div>
            ) : loadError ? (
              <div className="px-3 py-2 text-[10px] text-red-700">
                {loadError}
              </div>
            ) : filtered.length === 0 ? (
              <div className="px-3 py-2 text-[10px] text-slate-500">
                {(users ?? []).length === 0
                  ? 'Backlog ユーザーがありません。'
                  : '一致するユーザーがありません。'}
              </div>
            ) : (
              filtered.map((u) => {
                const taken = isUserTakenByOther(u.id);
                const isCurrent = current?.id === u.id;
                return (
                  <button
                    key={u.id}
                    type="button"
                    disabled={saving || (taken && !isCurrent)}
                    onClick={() =>
                      void handlePick({ id: u.id, name: u.name })
                    }
                    className={cn(
                      'block w-full px-3 py-1 text-left hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50',
                      isCurrent ? 'bg-slate-50 font-medium' : '',
                    )}
                    data-testid={`backlog-user-option-${u.id}`}
                  >
                    {u.name}{' '}
                    <span className="text-slate-400">({u.id})</span>
                    {taken && !isCurrent ? '（他の担当者に紐付け済）' : ''}
                  </button>
                );
              })
            )}
          </div>
          {saving ? (
            <div
              className="flex items-center gap-1 border-t border-slate-100 px-3 py-1 text-[10px] text-slate-500"
              data-testid="backlog-user-saving"
            >
              <Spinner className="h-3 w-3" />
              <span>保存中…</span>
            </div>
          ) : null}
          {err ? (
            <div
              className="border-t border-red-100 bg-red-50 px-3 py-1 text-[10px] text-red-700"
              data-testid="backlog-user-error"
            >
              {err}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Review panel for the "Backlog ユーザーを自動マッチ" flow. Lists each
 * fuzzy-matched proposal with a checkbox so the admin can untick a
 * questionable one (ambiguous prefix matches start unticked) before the
 * bulk apply. Nothing is written until 適用 is clicked.
 */
export function BacklogAutoMatchPanel({
  proposals,
  busy,
  onToggle,
  onApply,
  onCancel,
}: {
  proposals: AutoMatchProposal[];
  busy: boolean;
  onToggle: (index: number) => void;
  onApply: () => void;
  onCancel: () => void;
}) {
  const acceptedCount = proposals.filter((p) => p.accepted).length;
  return (
    <div
      className="mt-3 rounded border border-blue-200 bg-blue-50/50 p-3"
      data-testid="backlog-auto-match-panel"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700">
          自動マッチ候補（{proposals.length} 件）
        </h3>
        <span className="text-[11px] text-slate-500">
          チェックした {acceptedCount} 件を紐付けます
        </span>
      </div>
      <ul className="mt-2 divide-y divide-blue-100">
        {proposals.map((p, i) => (
          <li
            key={p.assigneeId}
            className="flex items-center gap-2 py-1.5 text-xs"
            data-testid={`backlog-auto-match-row-${p.assigneeId}`}
          >
            <input
              type="checkbox"
              checked={p.accepted}
              disabled={busy}
              onChange={() => onToggle(i)}
              data-testid={`backlog-auto-match-check-${p.assigneeId}`}
            />
            <span className="font-medium text-slate-700">{p.assigneeName}</span>
            <span className="text-slate-400">→</span>
            <span className="text-slate-700">
              {p.backlogUserName}{' '}
              <span className="text-slate-400">({p.backlogUserId})</span>
            </span>
            <span
              className={cn(
                'ml-auto rounded px-1.5 py-0.5 text-[10px]',
                p.matchType === 'exact'
                  ? 'bg-emerald-100 text-emerald-700'
                  : p.matchType === 'prefix'
                    ? 'bg-sky-100 text-sky-700'
                    : 'bg-amber-100 text-amber-800',
              )}
            >
              {p.matchType === 'exact'
                ? '完全一致'
                : p.matchType === 'prefix'
                  ? '前方一致'
                  : '曖昧（要確認）'}
            </span>
          </li>
        ))}
      </ul>
      <div className="mt-2 flex items-center gap-2">
        <Button
          disabled={busy || acceptedCount === 0}
          onClick={onApply}
          data-testid="backlog-auto-match-apply"
        >
          {busy ? '適用中…' : `適用（${acceptedCount} 件）`}
        </Button>
        <Button
          variant="ghost"
          disabled={busy}
          onClick={onCancel}
          data-testid="backlog-auto-match-cancel"
        >
          キャンセル
        </Button>
      </div>
    </div>
  );
}

/**
 * Modal for the "Backlog ユーザー一覧から担当者を追加" shortcut (spec §6):
 * onboard people who exist in Backlog but have no Migratus assignee yet.
 * Backlog users already linked to an assignee are hidden; ones whose name
 * already matches an existing assignee are flagged so the admin reaches for
 * 自動マッチ / the per-row picker instead of creating a duplicate.
 */
export function BacklogUserAddModal({
  users,
  loading,
  loadError,
  busy,
  alreadyLinkedBacklogIds,
  existingAssigneeNames,
  onAdd,
  onClose,
}: {
  users: BacklogUserListItem[] | null;
  loading: boolean;
  loadError: string | null;
  busy: boolean;
  alreadyLinkedBacklogIds: ReadonlyMap<number, string>;
  existingAssigneeNames: readonly string[];
  onAdd: (selected: BacklogUserListItem[]) => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<ReadonlySet<number>>(new Set());

  const existingKeys = useMemo(
    () => new Set(existingAssigneeNames.map((n) => stripAssigneeName(n))),
    [existingAssigneeNames],
  );

  // Hide Backlog users already linked to some assignee — they're done.
  const selectable = (users ?? []).filter(
    (u) => !alreadyLinkedBacklogIds.has(u.id),
  );
  const needle = q.trim().toLowerCase();
  const filtered = selectable.filter(
    (u) =>
      needle === '' ||
      u.name.toLowerCase().includes(needle) ||
      String(u.id).includes(needle),
  );

  const toggle = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const chosen = selectable.filter((u) => selected.has(u.id));

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      data-testid="backlog-add-modal"
    >
      <div className="flex max-h-[80vh] w-full max-w-lg flex-col rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h3 className="text-sm font-semibold text-slate-700">
            Backlog ユーザーから担当者を追加
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
            aria-label="閉じる"
          >
            <XCircle className="h-5 w-5" />
          </button>
        </div>
        <div className="border-b border-slate-100 px-4 py-2">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Backlog ユーザーを検索…"
            className="h-8 text-sm"
            data-testid="backlog-add-search"
          />
        </div>
        <div className="flex-1 overflow-auto px-2 py-1">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-6 text-xs text-slate-500">
              <Spinner className="h-4 w-4" />
              読み込み中…
            </div>
          ) : loadError ? (
            <div className="px-3 py-6 text-center text-xs text-red-700">
              {loadError}
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-3 py-6 text-center text-xs text-slate-500">
              追加できる Backlog ユーザーがありません。
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {filtered.map((u) => {
                const dup = existingKeys.has(stripAssigneeName(u.name));
                return (
                  <li key={u.id}>
                    <label className="flex cursor-pointer items-center gap-2 px-2 py-1.5 text-xs hover:bg-slate-50">
                      <input
                        type="checkbox"
                        checked={selected.has(u.id)}
                        disabled={busy}
                        onChange={() => toggle(u.id)}
                        data-testid={`backlog-add-check-${u.id}`}
                      />
                      <span className="text-slate-700">{u.name}</span>
                      <span className="text-slate-400">({u.id})</span>
                      {dup ? (
                        <span className="ml-auto rounded bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-800">
                          同名の担当者あり
                        </span>
                      ) : null}
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-4 py-3">
          <Button variant="ghost" disabled={busy} onClick={onClose}>
            キャンセル
          </Button>
          <Button
            disabled={busy || chosen.length === 0}
            onClick={() => onAdd(chosen)}
            data-testid="backlog-add-apply"
          >
            {busy ? '追加中…' : `選択した ${chosen.length} 件を追加`}
          </Button>
        </div>
      </div>
    </div>
  );
}
